/**
 * fout-uitleg.js — een ruwe foutmelding uit de verwerkingspijplijn leesbaar maken.
 *
 * Wat er in `processing_runs.error_message` belandt is de `message` van de fout die de
 * verwerking afbrak. Dat is prima om mee te debuggen, maar het is geen zin die je aan iemand
 * laat zien: een rate limit komt binnen als
 *
 *   OpenAI API error: 429 - {"error":{"message":"Rate limit reached for gpt-4.1 … ","type":…}}
 *
 * -- vierhonderd tekens JSON waarvan de helft ruis is. Deze module haalt daar twee dingen uit:
 * een KORTE typering (wat is er gebeurd) en het DETAIL (wat zei de bron precies), plus waar
 * mogelijk een HINT over wat je eraan doet.
 *
 * De gevallen hieronder zijn niet verzonnen: het zijn de teksten die ProcessingService,
 * AIService, TranscriptionService en het Workers-platform werkelijk produceren. Een onbekende
 * fout wordt niet geraden maar ongewijzigd doorgegeven — liever een ruwe tekst dan een
 * verkeerde geruststelling.
 */

import { classifyHallucinationPrevention, hallucinationPreventionUserText } from '@mv/models/hallucinationPrevention';

/** De `error.message` uit een OpenAI-foutbody, of null als die er niet in zit. */
function openaiBericht(ruw) {
  const haakje = ruw.indexOf('{');
  if (haakje < 0) return null;
  try {
    const body = JSON.parse(ruw.slice(haakje));
    const m = body?.error?.message;
    return typeof m === 'string' && m.trim() ? m.trim() : null;
  } catch (_) {
    // Afgekapte of niet-JSON body: pak het stuk achter "message":" als dat er staat. De
    // sluitquote is optioneel -- juist bij een afgekapte melding ontbreekt hij, en dat is net
    // het geval waarin je de tekst het hardst nodig hebt.
    const m = /"message"\s*:\s*"((?:[^"\\]|\\.)*)("|$)/.exec(ruw);
    return m && m[1] ? m[1].replace(/\\"/g, '"') : null;
  }
}

/**
 * Werd deze opname tegengehouden door de hallucinatie preventie? De herkenning zelf staat in
 * @mv/models, zodat de veldapp hetzelfde ziet; hier komt de tekst voor de beheerpagina's bij.
 *
 * @returns {{kort, detail, hint, preventie: true, reden}|null}
 */
export function hallucinatiePreventie(ruw) {
  const p = classifyHallucinationPrevention(ruw);
  if (!p) return null;
  const kort = {
    'no-speech': 'geen spraak gehoord',
    'too-much-text': 'meer tekst dan er gesproken is',
    'vocabulary-echo': 'transcript is de voorbeeldtekst uit de woordenlijst',
    'stock-phrase': 'verzonnen standaardzin',
  }[p.reason];
  return {
    kort: `Hallucinatie preventie: ${kort}`,
    detail: p.detail,
    hint: 'Er is bewust geen boorprofiel gemaakt: zonder (genoeg) spraak verzint het '
      + 'transcriptiemodel een tekst, en daar zou een geloofwaardig maar verzonnen boorprofiel uit komen. '
      + 'Luister de opname na; is er wél iets gezegd, spreek hem dan opnieuw in.',
    preventie: true,
    reden: p.reason,
  };
}

/** De uitleg voor een gebruiker op de projectpagina: { title, text } of null. */
export { hallucinationPreventionUserText as preventieVoorGebruiker };

/**
 * @param {string|null|undefined} ruw  de opgeslagen foutmelding
 * @returns {{kort: string, detail: string, hint: string|null, ruw: string}|null}
 *          null als er geen melding is.
 */
export function foutUitleg(ruw) {
  if (ruw == null) return null;
  const tekst = String(ruw).trim();
  if (!tekst) return null;

  const maak = (kort, detail, hint = null) => ({ kort, detail: detail || tekst, hint, ruw: tekst });

  // Hallucinatie preventie gaat vóór alles: dat is geen storing maar een bewuste keuze om geen
  // boorprofiel te maken, en dat moet er op elke pagina anders uitzien dan een fout. De melding
  // kan in een omhulling zitten ('Transcription failed: …', 'Transcriptie afgekeurd: …').
  const preventie = hallucinatiePreventie(tekst);
  if (preventie) return { ...preventie, ruw: tekst };

  // Eerst de omhulling afpellen: 'Transcription failed: OpenAI API error: 429 - …' is een
  // 429, maar dan in de transcriptiestap. Stond deze test ná die van OpenAI, dan won de
  // staart van de kop en verdween de stap waarin het misging uit beeld.
  if (/^Transcription failed:/i.test(tekst)) {
    const binnen = foutUitleg(tekst.replace(/^Transcription failed:\s*/i, ''));
    return binnen
      ? { ...binnen, kort: `Transcriptie mislukt — ${binnen.kort.toLowerCase()}`, ruw: tekst }
      : maak('Transcriptie mislukt', tekst);
  }

  // ── OpenAI gaf een HTTP-fout ────────────────────────────────────────────────
  const http = /OpenAI API error:\s*(\d{3})/.exec(tekst);
  if (http) {
    const code = Number(http[1]);
    const bericht = openaiBericht(tekst);
    if (code === 429) {
      return maak('OpenAI-limiet bereikt (429)', bericht,
        'Te veel tokens of verzoeken per minuut. De verwerking probeert het zelf al opnieuw; '
        + 'blijft het gebeuren, dan is de piek groter dan het budget van je OpenAI-tier.');
    }
    if (code === 401 || code === 403) {
      return maak(`OpenAI weigerde de sleutel (${code})`, bericht,
        'Controleer OPENAI_API_KEY van deze omgeving.');
    }
    if (code === 400) {
      return maak('OpenAI weigerde het verzoek (400)', bericht,
        'Meestal het schema of een te lang transcript, niet de opname zelf.');
    }
    if (code >= 500) {
      return maak(`Serverfout bij OpenAI (${code})`, bericht,
        'Aan hun kant. Opnieuw verwerken helpt bijna altijd.');
    }
    return maak(`OpenAI gaf een fout (${code})`, bericht);
  }

  // ── Grenzen van het Workers-platform ────────────────────────────────────────
  if (/exceeded.*(CPU|resource limits)/i.test(tekst)) {
    return maak('Worker boven zijn limiet', tekst,
      'De verwerking gebruikte meer CPU dan de ingestelde limiet toestaat. Rekenen (parsen, '
      + 'opbouwen) telt mee; wachten op OpenAI niet.');
  }
  if (/row (read|write) limit|D1's free tier/i.test(tekst)) {
    return maak('Daglimiet van D1 bereikt', tekst,
      'Geldt per account, over alle bedrijfsdatabases samen -- niet per database.');
  }
  if (/Too many subrequests/i.test(tekst)) {
    return maak('Te veel subrequests', tekst,
      'Eén verwerking deed meer externe aanroepen dan het plan toestaat.');
  }

  // ── Configuratie: er is geen (bruikbaar) model om mee te draaien ────────────
  if (/Geen actieve promptversie|bestaat niet meer of is leeg|Geen systeemprompt meegegeven/i.test(tekst)) {
    return maak('Geen actieve promptversie', tekst,
      'Kies een versie voor dit model op /admin/settings/model.');
  }
  if (/OPENAI_API_KEY is not configured/i.test(tekst)) {
    return maak('Geen OpenAI-sleutel ingesteld', tekst,
      'Zet het secret OPENAI_API_KEY op deze omgeving.');
  }

  // ── De uitkomst was er wel, maar niet bruikbaar ─────────────────────────────
  if (/is not valid JSON|Unexpected token|Unexpected end of JSON|JSON at position/i.test(tekst)) {
    return maak('Antwoord van het model was geen geldige JSON', tekst,
      'Komt voor bij een afgekapt antwoord. De ruwe uitvoer staat hieronder bij stap 3.');
  }
  if (/Cannot read propert/i.test(tekst)) {
    return maak('Onverwacht antwoord van het model', tekst,
      'Het antwoord had niet de vorm die we verwachtten — zie de ruwe uitvoer.');
  }

  // ── De transcriptie is bewust afgebroken ───────────────────────────────────
  //
  // Geen storing maar een oordeel: het transcript deugde niet en plan B hielp niet. De reden
  // staat al in gewone taal in de melding zelf, dus die laten we staan; wat we toevoegen is
  // dat dit een KEUZE was en wat je ermee kunt.
  if (/^Transcriptie onbruikbaar:/i.test(tekst)) {
    return maak('Transcriptie afgebroken', tekst.replace(/^Transcriptie onbruikbaar:\s*/i, ''),
      'Het transcript is bewaard maar niet verwerkt tot een boorprofiel, want dat zou er '
      + 'geloofwaardig uitzien en nergens op slaan. Lees het transcript hieronder: klopt het '
      + 'tóch, corrigeer het dan en verwerk de opname opnieuw.');
  }

  // Een run die nooit is afgerond en achteraf is gesloten. Dit is geen fout IN de verwerking
  // maar het ontbreken van een afloop: de verwerking is halverwege opgehouden te bestaan.
  if (/^Verwerking nooit afgerond/i.test(tekst)) {
    return maak('Verwerking nooit afgerond', tekst,
      'Deze verwerking is halverwege gestopt zonder iets te melden — meestal doordat de Worker '
      + 'werd afgebroken of een aanroep nooit antwoord gaf. De opname zelf kan intussen door een '
      + 'latere poging wél verwerkt zijn; kijk naar de status van de opname. Zo niet: opnieuw '
      + 'verwerken, het transcript is bewaard.');
  }

  // ── Volgorde in de pijplijn ─────────────────────────────────────────────────
  if (/must be transcribed before processing/i.test(tekst)) {
    return maak('Nog geen transcript', tekst,
      'De opname is niet (of niet volledig) getranscribeerd; begin daar.');
  }
  if (/No target boring for domain recording/i.test(tekst)) {
    return maak('Geen doelboring voor deze opname', tekst,
      'Een peilbuis-, watermonster- of afwerkingsopname hoort bij een bestaande boring.');
  }

  // Niets herkend: de melding zelf is dan het beste wat we hebben.
  return maak(tekst.split('\n')[0].slice(0, 120), tekst);
}
